import React from 'react';
import { Select, Typography, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
// Types
import { CampaignSmPlatform } from 'api/graphql-global-types';
// Helpers
import { ENGAGEMENT_RATE_OPTIONS } from 'helpers/campaign';
import {
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
} from 'helpers/campaignEditModal';
// Utils
import {
  CUSTOM_SOCIAL_PLATFORM_OPTIONS,
  PlatformCriteriaItem,
  platformLabel,
} from 'helpers/customCampaignConstants';
// Styles
import styles from './CustomCampaignSocialFilters.module.scss';

const { Text } = Typography;
const { Option } = Select;

type CustomCampaignSocialFiltersProps = {
  platformCriteria: PlatformCriteriaItem[];
  setPlatformCriteria: React.Dispatch<
    React.SetStateAction<PlatformCriteriaItem[]>
  >;
};

const CustomCampaignSocialFilters = ({
  platformCriteria,
  setPlatformCriteria,
}: CustomCampaignSocialFiltersProps): JSX.Element => {
  const activePlatforms = platformCriteria.map((pc) => pc.platform);

  const handlePlatformToggle = (platform: CampaignSmPlatform) => {
    setPlatformCriteria((prev) => {
      const exists = prev.find((pc) => pc.platform === platform);
      if (exists) {
        return prev.filter((pc) => pc.platform !== platform);
      }
      return [
        ...prev,
        {
          platform,
          minFollowers: null,
          maxFollowers: null,
          minEngagementRate: null,
        },
      ];
    });
  };

  const updatePlatformCriteria = (
    platform: CampaignSmPlatform,
    field: keyof PlatformCriteriaItem,
    value: number | null
  ) => {
    setPlatformCriteria((prev) =>
      prev.map((pc) =>
        pc.platform === platform ? { ...pc, [field]: value } : pc
      )
    );
  };

  return (
    <div className={styles.filterGroup}>
      <h4 className={styles.sectionTitle}>Social Filters</h4>

      <div>
        <Text className={styles.fieldLabel}>Platforms</Text>
        <div className={styles.platformButtons}>
          {CUSTOM_SOCIAL_PLATFORM_OPTIONS.map((opt) => (
            <Button
              key={opt.platform}
              type={
                activePlatforms.includes(opt.platform) ? 'primary' : 'default'
              }
              onClick={() => handlePlatformToggle(opt.platform)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {platformCriteria.map((item) => (
        <div key={item.platform} className={styles.platformCriteriaRow}>
          <Text className={styles.platformCriteriaTitle}>
            {platformLabel(item.platform)}
          </Text>
          <div>
            <Text className={styles.fieldLabel}>Min followers</Text>
            <Select
              placeholder="Min"
              allowClear
              style={{ width: '100%' }}
              value={item.minFollowers ?? undefined}
              onChange={(value) =>
                updatePlatformCriteria(
                  item.platform,
                  'minFollowers',
                  value ?? null
                )
              }
            >
              {FOLLOWING_OPTIONS_MIN.map((opt) => (
                <Option value={opt.value} key={`min-${opt.value}`}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Text className={styles.fieldLabel}>Max followers</Text>
            <Select
              placeholder="Max"
              allowClear
              style={{ width: '100%' }}
              value={item.maxFollowers ?? undefined}
              onChange={(value) =>
                updatePlatformCriteria(
                  item.platform,
                  'maxFollowers',
                  value ?? null
                )
              }
            >
              {FOLLOWING_OPTIONS_MAX.map((opt) => (
                <Option value={opt.value} key={`max-${opt.value}`}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Text className={styles.fieldLabel}>Min engagement rate</Text>
            <Select
              placeholder="Select rate"
              allowClear
              style={{ width: '100%' }}
              value={item.minEngagementRate ?? undefined}
              onChange={(value) =>
                updatePlatformCriteria(
                  item.platform,
                  'minEngagementRate',
                  value ?? null
                )
              }
            >
              {ENGAGEMENT_RATE_OPTIONS.map((opt) => (
                <Option value={opt.value} key={`er-${opt.value}`}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => handlePlatformToggle(item.platform)}
          />
        </div>
      ))}
    </div>
  );
};

export default CustomCampaignSocialFilters;
